import axios from "axios";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshTokenFn = async () => {
  const localRefreshToken = localStorage.getItem("refreshToken");
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/user/refresh-token`,
    { refreshToken: localRefreshToken }
  );
  const { accessToken } = response.data;

  localStorage.setItem("accessToken", accessToken);
  return accessToken;
};

const inFlightRequests = new Set();

const requestHistory = {};

axiosInstance.interceptors.request.use(
  async (config) => {
    const requestKey = JSON.stringify({
      method: config.method,
      url: config.url,
      params: config.params,
      data: config.data,
    });

    if (config._retry) {
      inFlightRequests.add(requestKey);
      return config;
    }

    if (inFlightRequests.has(requestKey)) {
      return Promise.reject(new axios.Cancel("Request already in flight"));
    }

    if (requestHistory[requestKey]) {
      const timeSinceLastRequest = Date.now() - requestHistory[requestKey];
      if (timeSinceLastRequest < 1000) {
        return Promise.reject(new axios.Cancel("Request debounced"));
      }
    }

    inFlightRequests.add(requestKey);

    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers["Authorization"] = `JWT ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    const requestKey = JSON.stringify({
      method: response.config.method,
      url: response.config.url,
      params: response.config.params,
      data: response.config.data,
    });

    inFlightRequests.delete(requestKey);

    requestHistory[requestKey] = Date.now();

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest) {
      const requestKey = JSON.stringify({
        method: originalRequest.method,
        url: originalRequest.url,
        params: originalRequest.params,
        data: originalRequest.data,
      });

      inFlightRequests.delete(requestKey);
    }

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `JWT ${token}`;

            originalRequest._retry = true;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshTokenFn();
        processQueue(null, newToken);
        originalRequest.headers["Authorization"] = `JWT ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");

        if (
          !["login", "register", "initial"].some((el) =>
            window.location.href.includes(el)
          )
        ) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

setInterval(() => {
  const now = Date.now();
  for (const key in requestHistory) {
    if (now - requestHistory[key] > 5000) {
      delete requestHistory[key];
    }
  }
}, 10000);

export default axiosInstance;
